import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatCheckbox } from '@angular/material/checkbox';
import { ErrorStateMatcher } from '@angular/material/core';
import { Router } from '@angular/router';
import { AppDataService, AuthService, MerchantService, OperatorService } from 'app/services';
import { AppComponent, RootEnum } from 'app/app.component';
import { BroadcastChannelTypeEnum, XpressUserRoleEnum } from 'app/models';
import { MessageService } from 'primeng/api';
import { AuthMethodEnum } from 'app/models/enums/auth-method.enum';
import { OperatorDashboardTourService } from 'app/components/operator/dashboard/operator-dashboard-tour.service';

const passwordResetSessionKey = 'operatorPasswordResetSession';
interface PasswordResetSession { processId: string; expiresAt: string; purpose: 'password-reset' }

export enum LoginForm {
    Base = 1,
    ResetPassword = 2,
    ResetSent = 3,
    OTPSent = 4,
    ResetOtp = 5,
    ResetSession = 6,
}

@Component({
    selector: 'operator-login',
    templateUrl: './operator-login.component.html',
    styleUrls: ['./operator-login.component.scss']
})
export class OperatorLoginComponent implements OnInit, OnDestroy {

    @ViewChild('usernameField', { read: ElementRef }) usernameField?: ElementRef<HTMLElement>;
    @ViewChild('password', { read: ElementRef }) password?: ElementRef<HTMLElement>;
    @ViewChild('rememberMe') rememberMe!: MatCheckbox;

    @ViewChild('slide1') slide1: any;
    @ViewChild('slide2') slide2: any;
    @ViewChild('slide3') slide3: any;
    @ViewChild('slide4') slide4: any;


    buildNumber: string | null = '';
    apiBaseUrl: string | null  = '';
    apiVersion: string | null  = '';
    apiUrl: string | null = '';
    usernameValue = '';
    passwordValue = '';
    isFirstLogin: boolean = false;
    carouselInterval: any;
    currentSlide = 1;
    sendOTPProcessId: string = '';
    loginForm: LoginForm = LoginForm.Base;
    operatorEmail: string | null = '';
    operatorPassword: string | null = '';
    rememberMeVal: boolean | null = false;
    otpValue = '';
    secondsToResend: number = 180;
    resendInterval: any;
    otpBlockInterval: any;
    secondsOtpBlock: number = 0;
    wrongOtpAttemptCount: number = 0;
    portalType: string = '';
    isLoadingOverlay = false;
    usernameError: string | null = null;
    passwordError: string | null = null;
    credentialsError: string | null = null;
    usernameBlankHighlight = false;
    passwordBlankHighlight = false;
    usernameFieldBlink = false;
    passwordFieldBlink = false;
    private pendingBlankFields: Array<'username' | 'password'> = [];
    private blankFieldBlinkTimer: ReturnType<typeof setTimeout> | null = null;
    emailError: string | null = null;
    otpError: string | null = null;
    otpInputError = false;
    recoveryEmail = '';
    recoveryEmailInvalid = false;
    maskedRecoveryEmail = '';
    passwordResetOtpExpiresAt = 0;
    newPassword = '';
    confirmPassword = '';
    showNewPassword = false;
    showConfirmPassword = false;
    passwordResetError: string | null = null;
    readonly operatorAccountNotFoundMessage =
        'We couldn’t find an operator account associated with that email address. Check the email and try again.';
    private readonly loginOtpResendSeconds = 180;

    /* The username has no validator, so Material's default matcher would never show its error. */
    usernameErrorMatcher: ErrorStateMatcher = { isErrorState: () => !!this.usernameError };

    constructor(
        private operatorService: OperatorService,
        private merchantService: MerchantService,
        private messageService: MessageService,
        public appData: AppDataService,
        public authService: AuthService,
        public app: AppComponent,
        private router: Router
    ) {}

    ngOnInit(): void {
        window.addEventListener('keypress', (e) => {
           if (this.loginForm === LoginForm.Base && e.key === 'Enter') {
               this.login();
           }
        });

        this.buildNumber = sessionStorage.getItem('BuildNumber');
        this.apiBaseUrl = sessionStorage.getItem('API_BaseUrl');
        this.apiVersion = sessionStorage.getItem('API_Version');

        const savedName = localStorage.getItem('savedName');
        const savedPass = localStorage.getItem('savedPass');
        const rememeberMe = localStorage.getItem('rememberMe') as unknown as boolean;

        if (savedName) {
            setTimeout(() => {
                this.usernameValue = savedName;
            }, 100);
        }
        if (savedPass) {
            setTimeout(() => {
                this.passwordValue = savedPass;
            }, 100);
        }
        if (rememeberMe) {
            setTimeout(() => {
                this.rememberMe.checked = true;
            }, 100);
        }

        if (this.isOperatorPortal) {
            this.carouselInterval = setInterval(() => {
                switch (this.currentSlide) {
                    case 1: this.slide2.nativeElement.checked = true; this.currentSlide = 2; break;
                    case 2: this.slide3.nativeElement.checked = true; this.currentSlide = 3; break;
                    case 3: this.slide4.nativeElement.checked = true; this.currentSlide = 4; break;
                    case 4: this.slide1.nativeElement.checked = true; this.currentSlide = 1; break;
                }
            }, 10000);
        }
        this.portalType = this.getRoleId() === XpressUserRoleEnum.Operator ? 'Operator' : 'Partner';
        this.clearStorage();
        this.restorePasswordResetSession();
    }

    ngOnDestroy(): void {
        clearInterval(this.carouselInterval);
        clearInterval(this.resendInterval);
        clearInterval(this.otpBlockInterval);
        this.clearBlankFieldBlinkTimer();
        this.clearStorage();
        localStorage.removeItem('userMail');
        localStorage.removeItem('processId');
    }

    clearStorage() {
        sessionStorage.removeItem('tourOperatorLogin');
        sessionStorage.removeItem('merchantLogin');
        sessionStorage.removeItem('operatorLogin');
        sessionStorage.removeItem('operatorSignUp');
    }

    goToSignUp() {
        if (window.location.pathname.startsWith('/operator')) {
            void this.router.navigate(['/operator/signup']);
            return;
        }

        this.app.operatorSignUp = true;
        this.app.operatorLogin = false;
        this.app.activeRoot = RootEnum.OperatorSignUp;
    }

    failedValidation(msg: string) {
        this.usernameError = msg;
        this.passwordError = null;
    }

    failedUsernameValidation() {
        this.usernameError = 'Username does not exist';
        this.passwordError = null;
    }

    failedPassValidation() {
        this.passwordError = 'Incorrect credentials. Please try again';
        this.usernameError = null;
    }

    otherError(errMsg: string) {
        this.passwordError = errMsg;
    }

    onUsernameInput() {
        this.usernameError = null;
        this.clearBlankField('username');
        this.clearIncorrectCredentialsError();
    }

    onPasswordInput() {
        this.passwordError = null;
        this.clearBlankField('password');
        this.clearIncorrectCredentialsError();
    }

    failedEmailValidation() {
        this.emailError = 'This email is not valid';
    }

    switchLoginForm(form: LoginForm) {
        if (this.loginForm === LoginForm.ResetOtp && form !== LoginForm.ResetOtp) {
            // Password-reset lockout uses attempt count with secondsOtpBlock left at 0
            // (no unlock timer). Clear it when leaving ResetOtp so shared login OTP
            // inputs are not stuck disabled for the rest of the page session.
            this.clearOtpAttemptLockState();
        }
        this.loginForm = form;
        this.usernameError = null;
        this.passwordError = null;
        this.clearIncorrectCredentialsError();
        this.clearBlankFieldHighlight();
    }

    openForgotPassword() {
        this.emailError = null;
        this.recoveryEmailInvalid = false;
        this.recoveryEmail = this.isOperatorPortal
            ? this.usernameValue.trim()
            : '';
        this.switchLoginForm(LoginForm.ResetPassword);
    }

    onRecoveryEmailInput() {
        this.recoveryEmailInvalid = false;
        this.emailError = null;
    }

    validateEmail(): boolean {
        const email = this.recoveryEmail.trim();
        if (!email) {
            return false;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            this.recoveryEmailInvalid = true;
            this.failedEmailValidation();
            return false;
        }

        if (this.emailError === 'This email is not valid') {
            this.emailError = null;
            this.recoveryEmailInvalid = false;
        }

        return true;
    }

    getRoleId() {
        if (this.app.merchantLogin) {
            return XpressUserRoleEnum.Merchant;
        }
        else if (this.app.tourOperatorLogin) {
            return XpressUserRoleEnum.TourOperator;
        }

        return XpressUserRoleEnum.Operator;
    }

    submitReset(force: boolean = false) {
        if (!force) {
            if (!this.validateEmail()) {
                return;
            }
        }

        this.showLoadingOverlay();
        const email = (force ? this.operatorEmail : this.recoveryEmail.trim()) ?? '';
        const dto = {
            Email: email,
            RoleId: this.getRoleId(),
        };

        const query = (this.app.merchantLogin || this.app.tourOperatorLogin)
            ? this.merchantService.resetPassword(dto, this.messageService)
            : this.operatorService.resetOperatorPassword(dto, this.messageService);

        query.then(data => {
                if (!data?.Success) {
                    this.hideLoadingOverlay();
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: data?.ErrorMessage });
                    return;
                }

                localStorage.setItem('userMail', email);
                localStorage.setItem('processId', data.ProcessId);

                this.switchLoginForm(LoginForm.ResetSent);
                this.hideLoadingOverlay();
            })
            .catch(err => {
                this.hideLoadingOverlay();
                throw err;
            });
    }

    sendPasswordResetOtp() {
        if (!this.validateEmail()) {
            return;
        }

        const email = this.recoveryEmail.trim();
        this.showLoadingOverlay();
        this.operatorService.resetOperatorPassword({
            Email: email,
            RoleId: this.getRoleId()
        }, this.messageService)
            .then(data => {
                this.hideLoadingOverlay();
                if (!data?.Success) {
                    if (this.isUnknownOperatorAccount(data?.ErrorMessage)) {
                        this.recoveryEmailInvalid = true;
                        this.emailError = this.operatorAccountNotFoundMessage;
                        return;
                    }

                    this.messageService.add({ severity: 'error', summary: 'Error', detail: data?.ErrorMessage });
                    return;
                }

                this.beginPasswordResetOtp(email, data.ProcessId, data.ExpiresInSeconds, data.RetryAfterSeconds);
            })
            .catch(err => {
                this.hideLoadingOverlay();
                throw err;
            });
    }

    get isOtpInputLocked(): boolean {
        if (this.secondsOtpBlock > 0) {
            return true;
        }

        // Password-reset exhaustion keeps secondsOtpBlock at 0 on purpose (request a
        // new OTP instead of a suspension timer). Only lock inputs on that form so
        // a leftover attempt count cannot hard-lock shared login OTP inputs.
        return this.loginForm === LoginForm.ResetOtp && this.wrongOtpAttemptCount >= 3;
    }

    setWrongOtpError() {
        if (this.secondsOtpBlock === 0) {
            this.secondsOtpBlock = 300;
            this.setOtpBlockTimer();
        }
    }

    private clearOtpAttemptLockState(): void {
        this.wrongOtpAttemptCount = 0;
        this.secondsOtpBlock = 0;
        clearInterval(this.otpBlockInterval);
        this.otpError = null;
        this.otpInputError = false;
    }

    setResendTimer() {
        clearInterval(this.resendInterval);
        this.resendInterval = setInterval(() => {
            if (this.secondsToResend > 0) {
                this.secondsToResend -= 1;
            }
        }, 1000);
    }

    setOtpBlockTimer() {
        clearInterval(this.otpBlockInterval);
        this.otpBlockInterval = setInterval(() => {
            if (this.secondsOtpBlock > 0) {
                this.secondsOtpBlock -= 1;
            } else if (this.secondsOtpBlock === 0) {
                this.wrongOtpAttemptCount = 0;
                clearInterval(this.otpBlockInterval);
            }
        }, 1000);
    }

    resendOTP() {
        if (this.secondsToResend > 0) {
            return;
        }

        const isPasswordReset = this.loginForm === LoginForm.ResetOtp;
        if (isPasswordReset && this.wrongOtpAttemptCount >= 3) {
            this.requestReplacementPasswordResetOtp();
            return;
        }

        const dto = {
            ProcessId: this.sendOTPProcessId,
            BroadcastChannelType: BroadcastChannelTypeEnum.Email
        };

        this.showLoadingOverlay();
        this.operatorService.resendOperatorOTP(dto, this.messageService)
            .then(data => {
                if (!data.Success) {
                    if (isPasswordReset && this.isOtpNotFoundError(data.ErrorMessage)) {
                        this.hideLoadingOverlay();
                        this.requestReplacementPasswordResetOtp();
                        return;
                    }

                    if (isPasswordReset) {
                        const retryAfterSeconds = this.serverSeconds(data.RetryAfterSeconds);
                        if (retryAfterSeconds > 0) {
                            this.secondsToResend = retryAfterSeconds;
                            this.setResendTimer();
                        }
                    }

                    this.otpError = data.ErrorMessage;
                    this.hideLoadingOverlay();
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
                    return;
                }
                this.secondsToResend = isPasswordReset
                    ? this.serverSeconds(data.RetryAfterSeconds)
                    : this.loginOtpResendSeconds;
                if (isPasswordReset) {
                    this.passwordResetOtpExpiresAt = this.expiryFromServerSeconds(data.ExpiresInSeconds);
                    this.wrongOtpAttemptCount = 0;
                    this.secondsOtpBlock = 0;
                }
                this.sendOTPProcessId = data.ProcessId;
                this.otpError = null;
                this.otpInputError = false;
                this.otpValue = '';
                this.setResendTimer();
                this.hideLoadingOverlay();
            })
            .catch(err => {
                this.hideLoadingOverlay();
                throw err;
            });
    }

    private requestReplacementPasswordResetOtp() {
        const email = (this.operatorEmail || this.recoveryEmail || '').trim();
        if (!email) {
            const otpError = 'Unable to request a new OTP. Return to log in and try again.';
            this.otpError = otpError;
            this.messageService.add({ severity: 'error', summary: 'Error', detail: otpError });
            return;
        }
        this.showLoadingOverlay();
        this.operatorService.resetOperatorPassword({ Email: email, RoleId: this.getRoleId() }, this.messageService)
            .then(data => {
                this.hideLoadingOverlay();
                if (!data?.Success) {
                    const otpError = data?.ErrorMessage || 'Failed to request a new OTP.';
                    this.otpError = otpError;
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: otpError });
                    return;
                }
                this.beginPasswordResetOtp(email, data.ProcessId, data.ExpiresInSeconds, data.RetryAfterSeconds);
                this.otpValue = '';
            })
            .catch(err => {
                this.hideLoadingOverlay();
                throw err;
            });
    }

    private isOtpNotFoundError(errorMessage: string | null | undefined): boolean {
        const normalized = (errorMessage ?? '').toLowerCase();
        return normalized.includes('otp not found') || normalized.includes('one-time password not found');
    }

    validateLogPass(): boolean {
        if (this.isOperatorPortal && (this.isBlank(this.usernameValue) || this.isBlank(this.passwordValue))) {
            const blankFields: Array<'username' | 'password'> = [];
            if (this.isBlank(this.usernameValue)) {
                blankFields.push('username');
            }
            if (this.isBlank(this.passwordValue)) {
                blankFields.push('password');
            }
            this.blinkBlankLoginFields(blankFields);
            return false;
        }

        if (!this.usernameValue) {
            this.failedUsernameValidation();
            return false;
        }
        if (!this.passwordValue) {
            if (this.credentialsError) {
                this.focusPasswordField();
                return false;
            }
            this.failedPassValidation();
            return false;
        }

        this.clearBlankFieldHighlight();
        this.operatorEmail = this.usernameValue;
        this.operatorPassword = this.passwordValue;
        this.rememberMeVal = this.rememberMe.checked;

        if (this.rememberMe.checked) {
            localStorage.setItem('savedName', this.usernameValue);
            localStorage.setItem('savedPass', this.passwordValue);
            localStorage.setItem('rememberMe', 'true');
        } else {
            localStorage.removeItem('savedName');
            localStorage.removeItem('savedPass');
            localStorage.removeItem('rememberMe');
        }

        return true;
    }

    //Call api /Login to get ProcessId
    login() {
        if (!this.validateLogPass()) {
            return;
        }

        const role = this.getRoleId();
        this.apiUrl = this.apiBaseUrl+'' + this.apiVersion;
        const data = 'username=' + encodeURIComponent(this.operatorEmail+'') + '&password=' + encodeURIComponent(this.operatorPassword+'')
            + '&roleId=' + role + '&authMethod=' + AuthMethodEnum.Email;

        this.showLoadingOverlay();
        this.authService.login(this.apiUrl, data)
            .then(response => response.json())
            .then(tmp => {
                this.hideLoadingOverlay();

                if (tmp.error) {
                    if (this.isOperatorPortal
                        && (tmp.error_description === 'User not found' || tmp.error_description === 'Password is incorrect')) {
                        this.showIncorrectCredentialsError();
                        return;
                    }

                    switch (tmp.error_description) {
                        case 'User not found': this.failedUsernameValidation(); break;
                        case 'Password is incorrect': this.failedPassValidation(); break;
                        default: this.failedValidation(tmp.error_description || tmp.error); break;
                    }
                    return;
                }

                this.isFirstLogin = tmp.IsFirstLogin;
                this.sendOTPProcessId = tmp.ProcessId;
                this.secondsToResend = this.loginOtpResendSeconds;
                this.clearOtpAttemptLockState();
                this.otpValue = '';
                this.switchLoginForm(LoginForm.OTPSent);
                this.setResendTimer();
            });
    }

    //Call api CheckOneTimePassword for mark OTP as verified
    checkOneTimePassword(processId: string, password: string = '112233') {
        if (this.loginForm === LoginForm.ResetOtp
            && this.passwordResetOtpExpiresAt > 0
            && Date.now() > this.passwordResetOtpExpiresAt) {
            this.otpError = 'This code has expired. Request a new OTP.';
            this.otpInputError = true;
            return;
        }

        const checkOtpApiUrl = this.apiUrl + 'api/Otp/CheckOTP';

        this.showLoadingOverlay();
        this.authService.checkOneTimePassword(checkOtpApiUrl, processId, password)
        .then(response => response.json())
        .then(checkOtpResponse => {
            this.hideLoadingOverlay();
            if (checkOtpResponse.ErrorMessage) {
                if (checkOtpResponse.ErrorMessage.includes('Wrong One-Time password')) {
                    this.otpError = 'Invalid OTP. Please try again.';
                    this.otpValue = '';
                } else {
                    this.otpError = checkOtpResponse.ErrorMessage;
                }

                this.otpInputError = true;

                this.wrongOtpAttemptCount += 1;
                if (this.wrongOtpAttemptCount === 3) {
                    if (this.loginForm === LoginForm.ResetOtp) {
                        this.otpError = 'Too many incorrect codes. Request a new OTP or return to log in.';
                        this.secondsOtpBlock = 0;
                        this.otpValue = '';
                    } else {
                        this.setWrongOtpError();
                    }
                }
                this.messageService.add({ severity: 'error', summary: 'Error', detail: checkOtpResponse.ErrorMessage || 'Invalid OTP. Please try again.' });
                return;
            }

            this.otpInputError = false;
            this.otpError = null;

            if (this.loginForm === LoginForm.ResetOtp) {
                this.openPasswordResetSession(processId, checkOtpResponse.ExpiresInSeconds);
                return;
            }

            this.token(processId);
        });
    }

    private beginPasswordResetOtp(
        email: string,
        processId: string,
        expiresInSeconds?: number | null,
        resendAfterSeconds?: number | null
    ) {
        this.apiUrl = (this.apiBaseUrl ?? '') + (this.apiVersion ?? '');
        this.operatorEmail = email;
        this.maskedRecoveryEmail = this.maskEmail(email);
        this.sendOTPProcessId = processId;
        this.passwordResetOtpExpiresAt = this.expiryFromServerSeconds(expiresInSeconds);
        this.secondsToResend = this.serverSeconds(resendAfterSeconds);
        this.wrongOtpAttemptCount = this.secondsOtpBlock = 0;
        this.otpValue = '';
        this.otpError = null;
        this.otpInputError = false;
        this.switchLoginForm(LoginForm.ResetOtp);
        this.setResendTimer();
    }

    private openPasswordResetSession(processId: string, expiresInSeconds?: number | null) {
        const expiresAtMs = this.expiryFromServerSeconds(expiresInSeconds);
        if (!expiresAtMs) {
            this.restartPasswordReset('This password-reset session has expired. Request a new OTP to start again.');
            return;
        }

        const session: PasswordResetSession = {
            processId,
            expiresAt: new Date(expiresAtMs).toISOString(),
            purpose: 'password-reset'
        };
        sessionStorage.setItem(passwordResetSessionKey, JSON.stringify(session));
        this.newPassword = '';
        this.confirmPassword = '';
        this.passwordResetError = null;
        this.switchLoginForm(LoginForm.ResetSession);
    }

    get canResetPassword(): boolean {
        return this.meetsResetPasswordPolicy(this.newPassword) && this.newPassword === this.confirmPassword;
    }

    get passwordsDoNotMatch(): boolean {
        return this.confirmPassword.length > 0 && this.newPassword !== this.confirmPassword;
    }

    submitNewPassword() {
        if (!this.canResetPassword) {
            return;
        }

        const session = this.readPasswordResetSession();
        if (!session) {
            this.restartPasswordReset('This password-reset session has expired. Request a new OTP to start again.');
            return;
        }

        this.passwordResetError = null;
        this.showLoadingOverlay();
        this.operatorService.setOperatorPasswordFromResetSession({
            ProcessId: session.processId,
            NewPassword: this.newPassword,
            ConfirmPassword: this.confirmPassword
        }, this.messageService)
            .then(data => {
                this.hideLoadingOverlay();
                if (!data?.Success) {
                    if (this.isExpiredPasswordResetSession(data?.ErrorMessage)) {
                        this.restartPasswordReset('This password-reset session has expired. Request a new OTP to start again.');
                        return;
                    }
                    this.passwordResetError = data?.ErrorMessage || 'Failed to set the new password.';
                    return;
                }
                sessionStorage.removeItem(passwordResetSessionKey);
                this.newPassword = '';
                this.confirmPassword = '';
                this.messageService.add({ severity: 'success', summary: 'Password updated', detail: 'You can now log in with your new password.' });
                this.switchLoginForm(LoginForm.Base);
            })
            .catch(err => {
                this.hideLoadingOverlay();
                throw err;
            });
    }

    private restorePasswordResetSession() {
        const session = this.isOperatorPortal ? this.readPasswordResetSession() : null;
        if (!session) {
            if (this.isOperatorPortal) {
                sessionStorage.removeItem(passwordResetSessionKey);
            }
            return;
        }
        this.sendOTPProcessId = session.processId;
        this.switchLoginForm(LoginForm.ResetSession);
    }

    private readPasswordResetSession(): PasswordResetSession | null {
        try {
            const session = JSON.parse(sessionStorage.getItem(passwordResetSessionKey) ?? '') as PasswordResetSession;
            const expiresAt = Date.parse(session?.expiresAt);
            const valid = session?.purpose === 'password-reset' && !!session.processId && !Number.isNaN(expiresAt) && expiresAt > Date.now();
            return valid ? session : null;
        } catch {
            return null;
        }
    }

    private meetsResetPasswordPolicy(password: string): boolean {
        return password.length >= 8
            && /\p{Lu}/u.test(password)
            && /\p{Ll}/u.test(password)
            && /\p{Nd}/u.test(password)
            && /[^\p{L}\p{Nd}]/u.test(password);
    }

    private expiryFromServerSeconds(expiresInSeconds?: number | null): number {
        const expiresIn = Number(expiresInSeconds);
        if (!Number.isFinite(expiresIn) || expiresIn <= 0) {
            return 0;
        }

        return Date.now() + expiresIn * 1000;
    }

    private serverSeconds(seconds?: number | null): number {
        const value = Number(seconds);
        if (!Number.isFinite(value) || value <= 0) {
            return 0;
        }

        return Math.ceil(value);
    }

    private isExpiredPasswordResetSession(errorMessage: string | null | undefined): boolean {
        const message = (errorMessage ?? '').toLowerCase();
        return message.includes('expired')
            || message.includes('one-time password not found')
            || message.includes('has not been verified')
            || message.includes('request a new otp');
    }

    private restartPasswordReset(message: string): void {
        sessionStorage.removeItem(passwordResetSessionKey);
        this.newPassword = '';
        this.confirmPassword = '';
        this.passwordResetError = null;
        this.recoveryEmailInvalid = false;
        this.emailError = message;
        this.switchLoginForm(LoginForm.ResetPassword);
    }

    private isUnknownOperatorAccount(errorMessage: string | null | undefined): boolean {
        const message = (errorMessage ?? '').toLowerCase();
        return message.includes('operator account not found')
            || message.includes('user not found')
            || /user .+ not found/.test(message);
    }

    private maskEmail(email: string): string {
        const [localPart, domain] = email.split('@');
        if (!localPart || !domain) {
            return email;
        }

        if (localPart.length <= 1) {
            return `*@${domain}`;
        }

        const maskedLocal = `${localPart[0]}${'*'.repeat(Math.max(localPart.length - 2, 1))}${localPart[localPart.length - 1]}`;
        return `${maskedLocal}@${domain}`;
    }

    //Call api token with ProcessId for get auth data
    token(processId: string) {
        const tokenApiUrl = this.apiUrl + 'token';

        this.authService.token(tokenApiUrl, processId)
        .then(response => response.json())
        .then(data => {
            if (data.error) {
                this.passwordError = 'Failed to get token';
                this.hideLoadingOverlay();
                return;
            }
            const authData = {
                access_token: data.access_token,
                '.expires': data['.expires']
            };

            const authDataJson = JSON.stringify(authData);

            if (this.rememberMeVal) {
                localStorage.setItem('xpressAuth', authDataJson);
            } else {
                sessionStorage.setItem('xpressAuth', authDataJson);
            }

            this.app.processAuthentication(authDataJson);
            this.hideLoadingOverlay();

            if (this.getRoleId() === XpressUserRoleEnum.Operator) {
                if (this.isFirstLogin) {
                    OperatorDashboardTourService.markFirstLogin();
                } else {
                    OperatorDashboardTourService.clearFirstLogin();
                }
            } else if (this.isFirstLogin) {
                this.submitReset(true);
            }

            setTimeout(() => {
                if (this.getRoleId() === XpressUserRoleEnum.Operator) {
                    void this.router.navigate(['/operator/dashboard/home']);
                } else {
                    this.app.activeRoot = RootEnum.PartnerDashboardSwitch;
                }

                this.app.operatorLogin = false;
                this.app.merchantLogin = false;
                this.app.tourOperatorLogin = false;
            }, 500);

            this.clearStorage();
        });
    }

    get isOperatorPortal(): boolean {
        return this.getRoleId() === XpressUserRoleEnum.Operator;
    }

    private showIncorrectCredentialsError() {
        this.credentialsError = 'The email or password is incorrect. Please try again.';
        this.usernameError = null;
        this.passwordError = null;
        this.clearBlankFieldHighlight();
        this.focusPasswordField();
    }

    private clearIncorrectCredentialsError() {
        this.credentialsError = null;
    }

    private focusPasswordField() {
        setTimeout(() => {
            this.password?.nativeElement.querySelector('input')?.focus();
        });
    }

    private isBlank(value: string): boolean {
        return value.trim().length === 0;
    }

    private blinkBlankLoginFields(fields: Array<'username' | 'password'>): void {
        this.clearIncorrectCredentialsError();
        this.clearBlankFieldHighlight();
        this.usernameError = null;
        this.passwordError = null;
        this.pendingBlankFields = [...fields];

        const alertColor = '#000000';
        const restColor = '#FFFFFF';
        const sequence = [alertColor, restColor, alertColor];
        let step = 0;

        const tick = () => {
            if (this.pendingBlankFields.length === 0) {
                return;
            }

            const color = sequence[step];
            const isAlert = color === alertColor;
            const active = new Set(this.pendingBlankFields);
            this.usernameFieldBlink = active.has('username') && isAlert;
            this.passwordFieldBlink = active.has('password') && isAlert;
            for (const field of this.pendingBlankFields) {
                this.paintFieldOutline(field, color);
            }
            step++;

            if (step >= sequence.length) {
                this.blankFieldBlinkTimer = null;
                const finished = [...this.pendingBlankFields];
                this.pendingBlankFields = [];
                this.usernameFieldBlink = false;
                this.passwordFieldBlink = false;
                this.usernameBlankHighlight = finished.includes('username');
                this.passwordBlankHighlight = finished.includes('password');
                for (const field of finished) {
                    this.paintFieldOutline(field, alertColor);
                }
                return;
            }

            this.blankFieldBlinkTimer = setTimeout(tick, 200);
        };

        tick();
    }

    private clearBlankField(field: 'username' | 'password'): void {
        if (field === 'username') {
            this.usernameBlankHighlight = false;
            this.usernameFieldBlink = false;
        } else {
            this.passwordBlankHighlight = false;
            this.passwordFieldBlink = false;
        }

        this.pendingBlankFields = this.pendingBlankFields.filter(item => item !== field);
        if (this.pendingBlankFields.length === 0) {
            this.clearBlankFieldBlinkTimer();
        }

        this.paintFieldOutline(field, null);
    }

    private paintFieldOutline(field: 'username' | 'password', color: string | null): void {
        const host = field === 'username'
            ? this.usernameField?.nativeElement
            : this.password?.nativeElement.querySelector<HTMLElement>('mat-form-field');
        if (!host) {
            return;
        }

        const outlineVariables = [
            '--mdc-outlined-text-field-outline-color',
            '--mdc-outlined-text-field-hover-outline-color',
            '--mdc-outlined-text-field-focus-outline-color'
        ];
        for (const name of outlineVariables) {
            if (color) {
                host.style.setProperty(name, color);
            } else {
                host.style.removeProperty(name);
            }
        }

        this.paintOutlinePiece(host.querySelector('.mdc-notched-outline__leading'), color, ['top', 'bottom', 'left']);
        this.paintOutlinePiece(host.querySelector('.mdc-notched-outline__notch'), color, ['top', 'bottom']);
        this.paintOutlinePiece(host.querySelector('.mdc-notched-outline__trailing'), color, ['top', 'bottom', 'right']);
    }

    private paintOutlinePiece(node: Element | null, color: string | null, sides: Array<'top' | 'right' | 'bottom' | 'left'>): void {
        if (!node) {
            return;
        }

        const outline = node as HTMLElement;
        outline.style.removeProperty('border-color');
        outline.style.removeProperty('border-width');
        for (const side of ['top', 'right', 'bottom', 'left'] as const) {
            const prop = `border-${side}-color`;
            if (color && sides.includes(side)) {
                outline.style.setProperty(prop, color, 'important');
            } else {
                outline.style.removeProperty(prop);
            }
        }
    }

    private clearBlankFieldHighlight(): void {
        this.pendingBlankFields = [];
        this.clearBlankFieldBlinkTimer();
        this.usernameBlankHighlight = false;
        this.passwordBlankHighlight = false;
        this.usernameFieldBlink = false;
        this.passwordFieldBlink = false;
        this.paintFieldOutline('username', null);
        this.paintFieldOutline('password', null);
    }

    private clearBlankFieldBlinkTimer(): void {
        if (this.blankFieldBlinkTimer !== null) {
            clearTimeout(this.blankFieldBlinkTimer);
            this.blankFieldBlinkTimer = null;
        }
    }

    showLoadingOverlay() {
        this.isLoadingOverlay = true;
    }

    hideLoadingOverlay() {
        this.isLoadingOverlay = false;
    }

    protected readonly LoginForm = LoginForm;
}
